import React, { useEffect, useRef } from 'react';

/**
 * RECORDING A CALL HELD ON ANOTHER APP (Gordon, 26 October): Teams, Zoom, a
 * Slack huddle, a phone on speaker. Jarvis is not in the call, so the
 * browser records what it can hear of it: the microphone (this side) mixed
 * with the sound the computer is playing (the other side), into one track
 * for the same recorder, the same chunks and the same transcript as any
 * other recording.
 *
 * WHAT A BROWSER CAN CAPTURE, checked on 26 October 2026 against MDN
 * (api.MediaDevices.getDisplayMedia, audio_capture_support) and Chrome's
 * screen-sharing documentation (developer.chrome.com/docs/web-platform/
 * screen-sharing-controls):
 *
 *   Chrome and Edge, Windows and ChromeOS   the WHOLE SYSTEM'S audio, when
 *       the person shares "Entire screen" and ticks "Also share system
 *       audio"; or one browser tab's audio when they share a tab.
 *   Chrome and Edge, macOS and Linux        ONE BROWSER TAB's audio only -
 *       "On Linux and macOS, only the audio of a tab can be captured." So on
 *       a Mac the call has to be in a browser tab (Teams or Zoom on the web)
 *       for the other side to be heard; the desktop apps cannot be captured.
 *   Firefox, Safari                          no audio with getDisplayMedia
 *       at all; the microphone alone is what can be recorded.
 *
 * getDisplayMedia needs a video track in the request - `video: false` is a
 * TypeError by the spec - so video is asked for and its track STOPPED the
 * moment the stream arrives; nothing of the screen is kept. systemAudio:
 * 'include' (Chrome 105+) asks for the system-audio tick to be offered;
 * selfBrowserSurface: 'exclude' keeps Jarvis's own tab out of the list, so
 * nobody records the recorder. Whether the person actually ticked audio is
 * only known from the stream that comes back, which is why the screen shows
 * a meter for the other side and warns when there is none.
 *
 * Nothing here talks to the server. RoomRecorder owns the recording; this
 * opens the sources, mixes them, and draws the two meters.
 */

const UA = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
export const IS_MAC = /Macintosh|Mac OS X/.test(UA) && !/iPhone|iPad/.test(UA);
export const IS_WINDOWS = /Windows/.test(UA);
export const IS_PHONE = /iPhone|iPad|iPod|Android/.test(UA);
const IS_FIREFOX = /Firefox\//.test(UA);
const IS_SAFARI = /Safari\//.test(UA) && !/Chrome\/|Chromium\/|Edg\//.test(UA);

/** Can this browser capture another app's sound at all? */
export const captureSupported = () => typeof navigator !== 'undefined'
  && typeof navigator.mediaDevices?.getDisplayMedia === 'function' && !IS_PHONE;

/** The one or two sentences that say what to pick in the browser's own dialog, for this machine. */
export const captureHint = () => {
  if (IS_PHONE) return 'A phone cannot capture another app’s sound. Put the call on speaker and record with the microphone, or use a laptop.';
  if (IS_FIREFOX || IS_SAFARI) {
    return 'This browser cannot capture another app’s sound; only your microphone will be recorded. Chrome or Edge can capture the call itself.';
  }
  if (IS_MAC) {
    return 'On a Mac the browser can capture the sound of one browser TAB only, not the whole computer: run the call in a tab (Teams or Zoom on the web), '
      + 'then choose that tab and tick “Share tab audio”. The Teams and Zoom desktop apps cannot be captured here.';
  }
  if (IS_WINDOWS) {
    return 'Choose “Entire screen” and tick “Also share system audio” - that captures everything the computer plays, so the Teams, Zoom or Slack app '
      + 'is heard whichever window it is in. (Choosing a browser tab captures that tab’s sound only.)';
  }
  return 'Choose the tab or screen the call is in and tick the option to share its audio; on Linux only a browser tab’s audio can be captured.';
};

/** The value kept on the recording for what was mixed. */
const captureOf = (display) => {
  if (!display) return 'mic';
  const track = display.getAudioTracks()[0];
  const surface = track?.getSettings?.()?.displaySurface || display.getVideoTracks()[0]?.getSettings?.()?.displaySurface || null;
  return surface === 'browser' ? 'mic+tab' : 'mic+system';
};

/**
 * Open the two sources and mix them. `mic` is an already-open microphone
 * stream (the recorder's own, so the browser's red dot means one thing).
 * Answers { mixed, display, remote, capture, close }, where `remote` says
 * whether the other side is actually in the mix. Throws what getDisplayMedia
 * threw - NotAllowedError when the person pressed Cancel - and never leaves
 * a display track running on an error.
 */
export async function openCallCapture(mic, { onEnded = null } = {}) {
  const display = await navigator.mediaDevices.getDisplayMedia({
    video: true,
    audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
    // Hints Chrome honours (105+, 107+); other browsers ignore unknown keys.
    systemAudio: 'include',
    selfBrowserSurface: 'exclude',
    surfaceSwitching: 'include'
  });
  // Audio only: the picture goes the moment it arrives.
  display.getVideoTracks().forEach((t) => { try { t.stop(); } catch { /* gone */ } });
  const remote = display.getAudioTracks().length > 0;
  const capture = remote ? captureOf(display) : 'mic';
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) {
    display.getTracks().forEach((t) => { try { t.stop(); } catch { /* gone */ } });
    throw new Error('This browser has no Web Audio, so the two sounds cannot be mixed.');
  }
  const ctx = new Ctor();
  const out = ctx.createMediaStreamDestination();
  if (mic) ctx.createMediaStreamSource(mic).connect(out);
  if (remote) {
    // Only the audio goes into the graph: a stream with its video stopped.
    const audioOnly = new MediaStream(display.getAudioTracks());
    ctx.createMediaStreamSource(audioOnly).connect(out);
    display.getAudioTracks()[0].addEventListener('ended', () => { if (onEnded) onEnded(); });
  }
  const close = () => {
    try { display.getTracks().forEach((t) => t.stop()); } catch { /* gone */ }
    try { ctx.close(); } catch { /* closed */ }
  };
  return { mixed: out.stream, display, remote, capture, close };
}

/**
 * A level meter for one stream: a bar painted straight onto the element,
 * outside React, four times a frame too many. `onHeard` fires once when the
 * level first counts as a voice.
 */
export function LevelMeter({ stream, label, large = false, off = false, onHeard = null, heardLevel = 0.08, testId }) {
  const barRef = useRef(null);
  useEffect(() => {
    const bar = barRef.current;
    if (!stream || !bar) return undefined;
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return undefined;
    let stopped = false;
    let frame = 0;
    let ctx = null;
    let heardYet = false;
    try {
      ctx = new Ctor();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const data = new Uint8Array(analyser.fftSize);
      const tick = () => {
        if (stopped) return;
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i += 1) { const v = (data[i] - 128) / 128; sum += v * v; }
        const level = off ? 0 : Math.min(1, Math.sqrt(sum / data.length) * 4);
        bar.style.width = `${Math.round(level * 100)}%`;
        bar.parentElement?.setAttribute('data-level', String(Math.round(level * 100)));
        if (!heardYet && level > heardLevel) { heardYet = true; if (onHeard) onHeard(); }
        frame = requestAnimationFrame(tick);
      };
      tick();
    } catch { /* no Web Audio: a flat bar, the recording unaffected */ }
    return () => {
      stopped = true;
      if (frame) cancelAnimationFrame(frame);
      try { ctx?.close(); } catch { /* closed */ }
      if (bar && document.contains(bar)) bar.style.width = '0%';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream, off]);
  return (
    <div className={`rr-meter${large ? ' is-large' : ''}${off ? ' is-off' : ''}`} role="img" aria-label={label} data-level="0" data-meter={testId || undefined}>
      <span className="rr-meter-bar" ref={barRef} />
    </div>
  );
}
